import { useQuery } from '@tanstack/react-query'
import { CalendarDays, CalendarPlus, Check, Inbox, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGuardians } from '@/features/auth/guardians'
import { EmptyState } from '@/components/feedback/States'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { FilterBar, SearchInput } from '@/components/tables/FilterBar'
import { Pagination } from '@/components/tables/Pagination'
import { Avatar } from '@/components/ui/Avatar'
import { Button, IconButton } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { Can } from '@/features/auth/guards'
import {
  leaveCalendarQuery,
  leaveCountsQuery,
  leaveRequestsQuery,
  leaveTypesQuery,
} from '@/features/leave/api'
import { DecisionDialog } from '@/features/leave/components/DecisionDialog'
import { FileLeaveDialog } from '@/features/leave/components/FileLeaveDialog'
import { LeaveStatusBadge, LeaveTypeBadge } from '@/features/leave/components/LeaveBadges'
import { LeaveCalendar } from '@/features/leave/components/LeaveCalendar'
import { LeaveRequestDrawer } from '@/features/leave/components/LeaveRequestDrawer'
import { monthBounds } from '@/features/leave/dates'
import { formatDays, formatRange, type LeaveRequest } from '@/features/leave/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useListParams } from '@/hooks/useListParams'
import { formatDate, formatNumber, humanize } from '@/utils/format'

const FILTERS = ['tab', 'kind', 'typeId'] as const
/** English labels (translation keys, ns `leave`); translated at render. */
const TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
] as const

export function LeaveRequestsPage() {
  const { t } = useTranslation('leave')
  const withGuardians = useGuardians()
  useDocumentTitle('Leave requests')
  const { state, query, update, toggleSort, setPage } = useListParams(FILTERS, { sort: 'startDate' })
  const tab = state.filters.tab ?? 'pending'
  const list = useQuery(
    leaveRequestsQuery({
      page: query.page,
      perPage: query.perPage,
      sort: query.sort,
      direction: query.direction,
      search: query.search,
      kind: state.filters.kind,
      typeId: state.filters.typeId,
      status: tab === 'all' ? undefined : tab,
    }),
  )
  const counts = useQuery(leaveCountsQuery())
  const types = useQuery(leaveTypesQuery())
  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1))
  const bounds = monthBounds(month)
  const calendar = useQuery(leaveCalendarQuery(bounds.from, bounds.to))
  const [openId, setOpenId] = useState<number | null>(null)
  const [deciding, setDeciding] = useState<{ request: LeaveRequest; decision: 'approve' | 'reject' } | null>(
    null,
  )
  const [filing, setFiling] = useState(false)

  const columns: Column<LeaveRequest>[] = [
    {
      key: 'person',
      header: t('Person'),
      cell: (r) => (
        <div className="flex items-center gap-3">
          <Avatar name={r.person.name} size="sm" />
          <div className="min-w-0">
            <p className="truncate font-medium text-ink">{r.person.name}</p>
            <p className="truncate text-xs text-muted">
              {[humanize(r.kind), r.person.detail ?? r.person.code].join(' · ')}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: t('Type'),
      sortKey: 'type',
      cell: (r) => <LeaveTypeBadge name={r.type.name} colour={r.type.colour} />,
    },
    {
      key: 'dates',
      header: t('Dates'),
      sortKey: 'startDate',
      cell: (r) => <span className="whitespace-nowrap">{formatRange(r)}</span>,
    },
    {
      key: 'days',
      header: t('Days'),
      sortKey: 'days',
      align: 'right',
      hideBelow: 'lg',
      cell: (r) => <span className="tabular">{formatNumber(r.days)}</span>,
    },
    {
      key: 'submitted',
      header: t('Submitted'),
      sortKey: 'createdAt',
      hideBelow: 'xl',
      cell: (r) => formatDate(r.createdAt),
    },
    { key: 'status', header: t('Status'), cell: (r) => <LeaveStatusBadge status={r.status} /> },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Actions')}</span>,
      align: 'right',
      cell: (r) =>
        r.status === 'pending' ? (
          <Can permission="leave.manage">
            <div
              className="flex justify-end gap-1"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              role="presentation"
            >
              <IconButton
                label={t('Approve leave for {{name}}', { name: r.person.name })}
                icon={<Check />}
                onClick={() => setDeciding({ request: r, decision: 'approve' })}
              />
              <IconButton
                label={t('Reject leave for {{name}}', { name: r.person.name })}
                icon={<X />}
                onClick={() => setDeciding({ request: r, decision: 'reject' })}
              />
            </div>
          </Can>
        ) : null,
    },
  ]

  const hasFilters = Boolean(state.search || state.filters.kind || state.filters.typeId)

  return (
    <>
      <PageHeader
        title={t('Leave requests')}
        description={t('Review and decide staff and student leave, and see who is away.')}
        breadcrumbs={[
          { label: t('Operations') },
          { label: t('Leave'), to: '/admin/leave' },
          { label: t('Requests') },
        ]}
        homeHref="/admin"
        actions={
          <Can permission="leave.manage">
            <Button leftIcon={<CalendarPlus />} onClick={() => setFiling(true)}>
              {t('File leave')}
            </Button>
          </Can>
        }
      />

      <Card>
        <div className="px-4 pt-2">
          <Tabs value={tab} onValueChange={(v) => update({ tab: v === 'pending' ? undefined : v })}>
            <TabsList aria-label={t('Filter by status')}>
              {TABS.map((lt) => (
                <TabsTrigger key={lt.value} value={lt.value} count={counts.data?.[lt.value]}>
                  {t(lt.label)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        <FilterBar
          actions={
            hasFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => update({ search: undefined, kind: undefined, typeId: undefined })}
              >
                {t('Clear filters')}
              </Button>
            ) : null
          }
        >
          <SearchInput
            value={state.search}
            onChange={(v) => update({ search: v })}
            placeholder={t('Name, code or reason')}
            label={t('Search leave requests')}
          />
          <Select
            aria-label={t('Filter by requester')}
            className="sm:w-auto sm:min-w-40"
            placeholder={t('Staff & students')}
            value={state.filters.kind ?? ''}
            onChange={(e) => update({ kind: e.target.value })}
            options={[
              { value: 'staff', label: t('Staff') },
              { value: 'student', label: t('Students') },
            ]}
          />
          <Select
            aria-label={t('Filter by leave type')}
            className="sm:w-auto sm:min-w-44"
            placeholder={t('All types')}
            value={state.filters.typeId ?? ''}
            onChange={(e) => update({ typeId: e.target.value })}
            options={(types.data ?? []).map((lt) => ({ value: lt.id, label: lt.name }))}
          />
        </FilterBar>
        <DataTable
          caption={t('Leave requests')}
          columns={columns}
          rows={list.data?.data}
          getRowId={(r) => r.id}
          loading={list.isPending}
          error={list.error}
          onRetry={() => void list.refetch()}
          sort={{ key: state.sort, direction: state.direction }}
          onSortChange={toggleSort}
          onRowClick={(r) => setOpenId(r.id)}
          mobileCard={(r) => (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <p className="truncate font-medium text-ink">{r.person.name}</p>
                <LeaveStatusBadge status={r.status} />
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
                <LeaveTypeBadge name={r.type.name} colour={r.type.colour} />
                <span>
                  {formatRange(r)} · {formatDays(r.days)}
                </span>
              </div>
            </div>
          )}
          empty={
            <EmptyState
              compact
              icon={<Inbox />}
              title={
                hasFilters
                  ? t('No requests match these filters')
                  : tab === 'pending'
                    ? t('Nothing waiting for a decision')
                    : t('No requests here yet')
              }
              description={
                tab === 'pending' && !hasFilters
                  ? withGuardians
                    ? t('New requests from staff and families will appear here.')
                    : t('New requests from staff and students will appear here.')
                  : undefined
              }
            />
          }
        />
        <Pagination
          meta={list.data?.meta}
          onPageChange={setPage}
          onPerPageChange={(n) => update({ perPage: n })}
        />
      </Card>

      <Card className="mt-6">
        <CardHeader
          title={t("Who's away")}
          description={t('Approved leave, with pending requests shown dashed.')}
          icon={<CalendarDays />}
        />
        <LeaveCalendar
          month={month}
          onMonthChange={setMonth}
          data={calendar.data}
          loading={calendar.isPending}
          onSelect={setOpenId}
        />
      </Card>

      {openId !== null ? (
        <LeaveRequestDrawer requestId={openId} onOpenChange={(o) => (o ? undefined : setOpenId(null))} />
      ) : null}
      {deciding ? (
        <DecisionDialog
          request={deciding.request}
          decision={deciding.decision}
          onOpenChange={(o) => (o ? undefined : setDeciding(null))}
        />
      ) : null}
      {filing ? <FileLeaveDialog onOpenChange={setFiling} /> : null}
    </>
  )
}
